/**
 * Vérification déterministe de l'objectif pédagogique : le déplacement par seconde est-il
 * le même à 5, 30 et 60 FPS ? (Sans `* Time.deltaTime`, il diffère d'un facteur 12.)
 * Aucun LLM, aucun backend : on rejoue le programme en headless.
 */

import type { Program } from './ast';
import { compile } from './compiler';
import type { SimError } from './errors';
import { runHeadless, InputProvider } from './interpreter';
import { SIM_FPS_VALUES, SimFps, SimInput, makeInput } from './runtime';
import type { KeyCode } from './whitelist';

export interface VerifyOptions {
  /** Durée simulée par cadence (défaut 3 s). */
  seconds?: number;
  /** Tolérance relative (défaut 0,10 = ±10 %). */
  tolerance?: number;
  /** Vitesse attendue en unités/seconde (ex. 5 pour `speed = 5f`). Absent → pas de contrôle de vitesse. */
  expectedPerSecond?: number;
  /** En dessous, l'objet est considéré immobile (défaut 0,05 u/s). */
  minPerSecond?: number;
  /** Entrée rejouée (défaut : flèche droite + D maintenues, `horizontal = 1`). */
  input?: SimInput | InputProvider;
}

export interface FpsMeasure {
  fps: SimFps;
  /** Distance parcourue dans le plan XY (unités Unity). */
  distance: number;
  /** Distance / secondes simulées. */
  perSecond: number;
  frames: number;
  error: SimError | null;
}

export interface VerifyResult {
  ok: boolean;
  perFps: Record<SimFps, FpsMeasure>;
  /** perSecond(60) / perSecond(5) ; `null` si l'objet ne bouge pas à 5 FPS. ≈ 12 sans deltaTime, ≈ 1 avec. */
  ratio60over5: number | null;
  /** Analyse statique : `Time.deltaTime` atteint `transform.position` / `transform.Translate`. */
  usesDeltaTime: boolean;
  /** Explication en français, destinée à l'étudiant. */
  reason: string;
  /** Erreurs de compilation ou d'exécution rencontrées (vide si tout a tourné). */
  errors: SimError[];
  warnings: string[];
}

const HELD: ReadonlySet<KeyCode> = new Set<KeyCode>(['RightArrow', 'D']);
const HELD_INPUT = makeInput({ horizontal: 1, vertical: 0, keys: HELD });
const FIRST_FRAME_INPUT = makeInput({ horizontal: 1, vertical: 0, keys: HELD, keysDown: HELD });

/** Flèche droite (et D) maintenues ; `GetKeyDown` vrai à la première frame seulement. */
export const DEFAULT_VERIFY_INPUT: InputProvider = (frame) => (frame === 1 ? FIRST_FRAME_INPUT : HELD_INPUT);

export const DEFAULT_VERIFY_SECONDS = 3;
export const DEFAULT_VERIFY_TOLERANCE = 0.1;

const fmt = (v: number): string => v.toFixed(2).replace('.', ',');

export function verifyFrameIndependence(source: string | Program, opts: VerifyOptions = {}): VerifyResult {
  const seconds = opts.seconds ?? DEFAULT_VERIFY_SECONDS;
  const tolerance = opts.tolerance ?? DEFAULT_VERIFY_TOLERANCE;
  const minPerSecond = opts.minPerSecond ?? 0.05;
  const inputOpt = opts.input ?? DEFAULT_VERIFY_INPUT;

  let program: Program;
  let warnings: string[] = [];
  if (typeof source === 'string') {
    const compiled = compile(source);
    if (!compiled.ok) {
      const first = compiled.errors[0];
      return {
        ok: false,
        perFps: emptyMeasures(),
        ratio60over5: null,
        usesDeltaTime: false,
        reason: `Le code ne compile pas (ligne ${first.line}) : ${first.message}`,
        errors: compiled.errors,
        warnings: compiled.warnings,
      };
    }
    program = compiled.program;
    warnings = compiled.warnings;
  } else {
    program = source;
  }

  const perFps = emptyMeasures();
  const errors: SimError[] = [];
  for (const fps of SIM_FPS_VALUES) {
    const run = runHeadless(program, { fps, seconds, input: inputOpt });
    const dx = run.state.position.x - run.startPosition.x;
    const dy = run.state.position.y - run.startPosition.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const simulated = run.seconds > 0 ? run.seconds : seconds;
    perFps[fps] = { fps, distance, perSecond: distance / simulated, frames: run.frames, error: run.error };
    if (run.error) errors.push(run.error);
  }

  const usesDeltaTime = program.usesDeltaTime;
  const p5 = perFps[5].perSecond;
  const p30 = perFps[30].perSecond;
  const p60 = perFps[60].perSecond;
  const ratio60over5 = p5 > 1e-9 ? p60 / p5 : null;

  if (errors.length > 0) {
    const first = errors[0];
    const fps = SIM_FPS_VALUES.find((f) => perFps[f].error === first) ?? 5;
    return {
      ok: false, perFps, ratio60over5, usesDeltaTime,
      reason: `Erreur pendant la simulation à ${fps} FPS (ligne ${first.line}) : ${first.message}`,
      errors, warnings,
    };
  }

  const values = [p5, p30, p60];
  const mean = (p5 + p30 + p60) / 3;
  const moving = Math.min(...values) >= minPerSecond;
  const consistent = mean > 0 && values.every((v) => Math.abs(v - mean) <= tolerance * mean);
  const expected = opts.expectedPerSecond;
  const expectedOk = expected === undefined || Math.abs(mean - expected) <= tolerance * expected;
  const measured = `${fmt(p5)} u/s à 5 FPS, ${fmt(p30)} u/s à 30 FPS, ${fmt(p60)} u/s à 60 FPS`;

  let reason: string;
  if (!moving && Math.max(...values) < minPerSecond) {
    reason =
      `L'objet ne bouge pas : la simulation a tourné ${seconds} s à 5, 30 et 60 FPS avec la flèche droite maintenue, ` +
      `et transform.position n'a pas changé. Lis les entrées (Input.GetAxisRaw("Horizontal")) et modifie transform.position dans Update().`;
  } else if (!consistent || !moving) {
    reason = usesDeltaTime
      ? `Le déplacement varie encore selon la cadence : ${measured}${ratio60over5 !== null ? ` (×${fmt(ratio60over5)} entre 5 et 60 FPS)` : ''}, ` +
        `alors que Time.deltaTime apparaît dans ton code. Vérifie qu'il est appliqué exactement une fois (pas deltaTime × deltaTime), ` +
        `sur tout le déplacement, et que l'objet n'est pas bloqué par un bord (Mathf.Clamp) pendant la mesure.`
      : `Le déplacement dépend de la cadence : ${measured}${ratio60over5 !== null ? ` (×${fmt(ratio60over5)} entre 5 et 60 FPS)` : ''}. ` +
        `Sans « * Time.deltaTime », tu avances de « speed » unités à CHAQUE image — et il y a 12 fois plus d'images à 60 FPS qu'à 5 FPS. ` +
        `Multiplie le déplacement par Time.deltaTime pour raisonner en unités par seconde, quelle que soit la cadence.`;
  } else if (!expectedOk) {
    reason =
      `Le déplacement est bien indépendant de la cadence (${measured}), mais la vitesse mesurée est ≈ ${fmt(mean)} u/s ` +
      `au lieu de ${fmt(expected as number)} u/s attendus : vérifie la valeur de « speed » et que la direction est normalisée (longueur 1).`;
  } else {
    reason =
      `Déplacement identique à 5, 30 et 60 FPS : ≈ ${fmt(mean)} u/s. ` +
      (usesDeltaTime
        ? `Time.deltaTime est bien appliqué : le mouvement est exprimé en unités par seconde, pas par image.`
        : `Le mouvement est cohérent entre les cadences, sans que Time.deltaTime atteigne transform.position d'après l'analyse — vérifie que ce n'est pas un hasard de ton calcul.`);
  }

  return { ok: moving && consistent && expectedOk, perFps, ratio60over5, usesDeltaTime, reason, errors, warnings };
}

function emptyMeasures(): Record<SimFps, FpsMeasure> {
  return {
    5: { fps: 5, distance: 0, perSecond: 0, frames: 0, error: null },
    30: { fps: 30, distance: 0, perSecond: 0, frames: 0, error: null },
    60: { fps: 60, distance: 0, perSecond: 0, frames: 0, error: null },
  };
}
